import { ReactElement, useEffect, useMemo } from 'react'
import { Vector3 } from 'three'
import { FontLoader } from 'three/addons/loaders/FontLoader.js'
import { useLoader } from '@react-three/fiber'
import arialFontUrl from 'src/assets/fonts/Arial_Regular.typeface.json?url'
import type { PdfTableLayout } from 'src/domain/planTables/tableLayout'
import { buildTableFillGeometry, buildTableTextGeometry } from './utils'

const CONTENT_RENDER_ORDER = 1000

interface Props {
  layout: PdfTableLayout
  ptToWorld: number
  /** World position of the table's bottom-left corner. */
  origin: Vector3
}

/**
 * The plan table exactly as the exported plan draws it (`make_table`): cell backgrounds, grid
 * lines and cell text, in two draw calls however many cells the table has. The geometry is built
 * relative to the table's corner, so moving the table (e.g. while dragging) only moves the group.
 * Suspends until the Arial typeface is loaded.
 */
const PlanTableContent = ({ layout, ptToWorld, origin }: Props): ReactElement => {
  const font = useLoader(FontLoader, arialFontUrl)

  const fillGeometry = useMemo(() => buildTableFillGeometry(layout, ptToWorld), [layout, ptToWorld])
  const textGeometry = useMemo(
    () => buildTableTextGeometry(layout, font, ptToWorld),
    [layout, font, ptToWorld],
  )

  // Release the GPU buffers of replaced geometries.
  useEffect(() => () => fillGeometry.dispose(), [fillGeometry])
  useEffect(() => () => textGeometry?.dispose(), [textGeometry])

  return (
    <group position={origin}>
      <mesh geometry={fillGeometry} renderOrder={CONTENT_RENDER_ORDER}>
        <meshBasicMaterial vertexColors toneMapped={false} depthTest={false} depthWrite={false} />
      </mesh>
      {textGeometry && (
        <mesh geometry={textGeometry} renderOrder={CONTENT_RENDER_ORDER + 1}>
          <meshBasicMaterial vertexColors toneMapped={false} depthTest={false} depthWrite={false} />
        </mesh>
      )}
    </group>
  )
}

export default PlanTableContent
